import { useCallback, useEffect, useRef } from "react";
import type { Ticket } from "@omnabase/shared";
import { firstNewMessageId } from "./threadEntries";

/** The polish round (2026-10-31; DECISIONS "The polish round…"): where the thread's "New" divider stands and the one jump to it. Decided once
 *  per visit, from the first read of this ticket (its `readAt`, the person's mark before that open): a live reload moves the mark but never
 *  the divider, and never scrolls again; another ticket (or leaving and coming back) decides anew. Nothing new, or never read: no divider, and
 *  the page stays at the top. The jump is a plain `scrollIntoView` (no smooth scroll), so reduced motion has nothing to follow.
 *  Ticket discussion round two (2026-11-02; the red team's finding 12, a round-one bug): the jump waits until `ready` (the ticket's discussion
 *  has loaded), because its lines stand inline above the new message and would push it out of view if they arrived after the jump. */
export function useFirstNew(ticket: Ticket | null, me: string, ready = true): { newId: string | undefined; newRef: (el: HTMLDivElement | null) => void } {
  const decided = useRef<{ number: number; id: string | null; jumped: boolean } | null>(null);
  if (ticket && decided.current?.number !== ticket.number) decided.current = { number: ticket.number, id: firstNewMessageId(ticket.messages, { readAt: ticket.readAt ?? null, me }), jumped: false };
  const divider = useRef<HTMLDivElement | null>(null);
  const isReady = useRef(ready); isReady.current = ready;
  const jump = useCallback(() => {
    const d = decided.current, el = divider.current;
    if (!el || !d || d.jumped || !isReady.current) return;
    d.jumped = true;
    el.scrollIntoView({ block: "start" });
  }, []);
  const newRef = useCallback((el: HTMLDivElement | null) => { divider.current = el; jump(); }, [jump]);
  useEffect(() => { if (ready) jump(); }, [ready, jump]); // the lines are in the page now (this effect runs after their render)
  return { newId: decided.current?.id ?? undefined, newRef };
}
